/**
 * The one place this application talks GraphQL, for the three calls it makes.
 *
 * **Attention, THIS IS THE ANSWER TO THE NOTE LEFT ON `activateAccount`, AND NOT A WAY
 * AROUND IT.** That note said the day a second call lands is the moment to bring Apollo
 * in. What it was really guarding against is written in its own reasoning: hand-written
 * queries multiply, each with its own `fetch`, its own error reading and its own idea of
 * what a refusal is. That is what this module removes, in twenty lines, without a
 * provider wrapped around every private route and without a codegen configuration to
 * maintain for three requests that want neither cache nor normalisation.
 *
 * So the threshold moves, and it moves to something checkable rather than to a count:
 * **the day this application needs a CACHE, a normalised store or subscriptions, Apollo
 * is the answer and this file is deleted.** Three calls that each want a fresh answer are
 * not that day.
 *
 * `client-hub` and `hub-admin` do carry Apollo, and rightly: they read lists, they
 * paginate, they refetch after mutations. This one signs people in.
 */

/** What a GraphQL endpoint answers, reduced to what a caller here reads. */
export interface GraphQLAnswer<T> {
    data?: T | null;
    errors?: { message: string; extensions?: { code?: string } }[];
}

export class ApiUnavailable extends Error {}

/**
 * Posts one operation and returns the raw answer, errors included.
 *
 * **It does not throw on a GraphQL error**, only when the endpoint could not be reached
 * at all: a refusal carrying a business code is an answer, and each caller words it for
 * its own screen. Flattening the two here is how a screen ends up saying « ça a planté »
 * about a refusal that named exactly what to do.
 */
export const graphql = async <T>(
    query: string,
    variables?: Record<string, unknown>,
    /** The idToken, for the calls that need one. This API verifies that one. */
    idToken?: string
): Promise<GraphQLAnswer<T>> => {
    const url = import.meta.env.PUBLIC_CLIENT_API_URL;
    if (!url) {
        // A missing configuration is not a refusal: telling the person their link is
        // invalid over a deployment mistake sends them hunting for a new invitation.
        throw new ApiUnavailable('PUBLIC_CLIENT_API_URL is not set');
    }

    let response: Response;
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
            },
            body: JSON.stringify({ query, variables }),
        });
    } catch (failure) {
        throw new ApiUnavailable(failure instanceof Error ? failure.message : 'network failure');
    }

    try {
        return (await response.json()) as GraphQLAnswer<T>;
    } catch {
        // A 502 from a proxy answers HTML: reading it as an empty result would make a
        // caller believe the server said « nothing », which is not what happened.
        throw new ApiUnavailable(`unreadable answer (HTTP ${response.status})`);
    }
};
